;(async () => {
  const root = document.querySelector('[data-msp-stove]');
  if (!root) return;

  const grid = root.querySelector('[data-stove-grid]');
  let cards = [...grid.querySelectorAll('[data-stove-product]')];
  const expectedCardCount = Number(root.dataset.collectionCount) || cards.length;
  const collectionPageSize = 250;
  const requestedPage = Math.max(1, Number(new URL(window.location.href).searchParams.get('page')) || 1);
  const handleForCard = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';
    try {
      return decodeURIComponent(new URL(link.href, window.location.origin).pathname.split('/products/')[1] || '');
    } catch {
      return '';
    }
  };
  cards.forEach((card, index) => { card.dataset.index = String((requestedPage - 1) * collectionPageSize + index); });
  if (cards.length < expectedCardCount) {
    const existingHandles = new Set(cards.map(handleForCard).filter(Boolean));
    const pageCount = Math.ceil(expectedCardCount / collectionPageSize);
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      if (pageNumber === requestedPage) continue;
      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.set('page', String(pageNumber));
      try {
        const response = await fetch(pageUrl, { headers: { Accept: 'text/html' } });
        if (!response.ok) break;
        const pageDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const pageCards = [...pageDocument.querySelectorAll('[data-msp-stove] [data-stove-grid] [data-stove-product]')];
        if (!pageCards.length) break;
        pageCards.forEach((pageCard, index) => {
          const handle = handleForCard(pageCard);
          if (handle && existingHandles.has(handle)) return;
          const clone = pageCard.cloneNode(true);
          clone.dataset.index = String((pageNumber - 1) * collectionPageSize + index);
          grid.append(clone);
          cards.push(clone);
          if (handle) existingHandles.add(handle);
        });
      } catch (error) {
        console.warn('MSP Ofenfinder konnte eine Kollektionsseite nicht laden.', error);
        break;
      }
    }
  }
  let orderedCards = [...cards];
  const numberListFrom = (value) => (String(value || '').match(/\d+(?:[.,]\d+)?/g) || [])
    .map((match) => Number(match.replace(',', '.')))
    .filter(Number.isFinite);
  const uniqueNumbersFrom = (value) => [...new Set(numberListFrom(value))];
  const diameterOptions = root.querySelector('[data-diameter-options]');
  const availableDiameters = [...new Set(cards.flatMap((card) => uniqueNumbersFrom(card.dataset.diameter)))].sort((a, b) => a - b);
  if (diameterOptions) {
    availableDiameters.forEach((diameter) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.filterValue = String(diameter);
      button.setAttribute('aria-pressed', 'false');
      button.textContent = `Ø ${String(diameter).replace('.', ',')} mm`;
      diameterOptions.append(button);
    });
  }
  const groups = [...root.querySelectorAll('[data-filter-group]')];
  const count = root.querySelector('[data-result-count]');
  const empty = root.querySelector('[data-stove-empty]');
  const reset = root.querySelector('[data-stove-reset]');
  const sort = root.querySelector('[data-stove-sort]');
  const more = root.querySelector('[data-stove-more]');
  const resultsHead = root.querySelector('.msp-stove-results-head');
  const activeFilters = root.querySelector('[data-active-filters]');
  const defaultSort = 'recommended';
  let resultLimit = 12;
  let finderOpen = false;

  const emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(`msp:stove-${name}`, { detail }));
  const powerValuesOf = (card) => uniqueNumbersFrom(card.dataset.power);
  const nominalPowerValuesOf = (card) => uniqueNumbersFrom(card.dataset.nominalPower);
  const groupNameFor = (value) => {
    if (['stove', 'storage', 'hybrid', 'water'].includes(value)) return 'kind';
    if (['xtra', 'hms', 'mineral', 'generic'].includes(value)) return 'storage';
    return 'storage';
  };

  const matches = (card, button) => {
    const value = button.dataset.filterValue;
    const group = button.closest('[data-filter-group]')?.dataset.filterGroup || groupNameFor(value);
    if (group === 'kind') {
      if (value === 'stove') return card.dataset.kind !== 'hybrid';
      if (value === 'storage') return card.dataset.storage !== 'none' && card.dataset.kind !== 'hybrid';
      if (value === 'water') return card.dataset.water === 'true';
      return card.dataset.kind === value;
    }
    if (group === 'room') return card.dataset.room === value;
    if (group === 'outlet') {
      const outlets = String(card.dataset.outlet || '').split('|');
      if (value === 'both') return outlets.includes('both') || (outlets.includes('top') && outlets.includes('rear'));
      if (value === 'top') return outlets.includes('top') || outlets.includes('both');
      if (value === 'rear') return outlets.includes('rear') || outlets.includes('both');
      return false;
    }
    if (group === 'diameter') return uniqueNumbersFrom(card.dataset.diameter).includes(Number(value));
    if (group === 'air') return value === 'external' ? card.dataset.externalAir === 'true' : value === 'dibt' ? card.dataset.dibt === 'true' : false;
    if (group === 'fuel') {
      const fuel = card.dataset.fuel.toLowerCase();
      if (value === 'hybrid') return fuel.includes('hybrid');
      if (value === 'coal') return fuel.includes('kohle');
      return value === 'wood' && fuel.includes('holz');
    }
    if (group === 'cladding') return card.dataset.cladding.split(' ').includes(value);
    if (group === 'budget') {
      const price = Number(card.dataset.price);
      if (value === 'to-2000') return price <= 200000;
      if (value === '2000-4000') return price > 200000 && price <= 400000;
      if (value === '4000-6000') return price > 400000 && price <= 600000;
      return value === 'over-6000' && price > 600000;
    }
    if (group === 'storage') {
      if (value === 'with') return card.dataset.storage !== 'none' && card.dataset.kind !== 'hybrid';
      if (value === 'xtra' || value === 'hms' || value === 'mineral' || value === 'generic') return card.dataset.storage === (value === 'generic' ? 'storage' : value);
      if (value === 'stone' || value === 'ceramic') return card.dataset.design.split(' ').includes(value);
      return false;
    }
    if (group === 'design') return card.dataset.design.split(' ').includes(value);
    if (group === 'vendor') return card.dataset.vendor === value;
    if (group === 'power') {
      const [minValue, maxValue] = value.split(':');
      const min = Number(minValue);
      const max = maxValue === '' ? null : Number(maxValue);
      return nominalPowerValuesOf(card).some((power) => power > min && (max === null || power <= max));
    }
    return true;
  };

  const activeFor = (group) => [...group.querySelectorAll('[data-filter-value][aria-pressed="true"]')];
  const closeGroup = (group, restoreFocus = false) => {
    const toggle = group?.querySelector('[data-group-toggle]');
    const panel = toggle?.nextElementSibling;
    if (!toggle || !panel) return;
    const wasOpen = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
    if (restoreFocus && wasOpen) toggle.focus({ preventScroll: true });
  };
  const closeGroups = (except = null) => groups.forEach((group) => {
    if (group !== except) closeGroup(group);
  });
  const apply = () => {
    const activeGroups = groups.map((group) => ({ group, active: activeFor(group) })).filter((item) => item.active.length);
    const matched = orderedCards.filter((card) => activeGroups.every(({ active }) => active.some((button) => matches(card, button))));
    const shouldShow = finderOpen || activeGroups.length > 0;
    let visible = 0;
    orderedCards.forEach((card) => {
      const match = matched.includes(card);
      const show = shouldShow && match && visible < resultLimit;
      card.hidden = !show;
      if (show) visible += 1;
    });
    grid.hidden = !shouldShow;
    resultsHead.classList.toggle('is-idle', !shouldShow);
    const incompleteNotice = cards.length < expectedCardCount ? ` – Daten unvollständig: ${cards.length} von ${expectedCardCount} geladen` : '';
    count.textContent = shouldShow ? `${matched.length} passende${matched.length === 1 ? 'r' : ''} ${matched.length === 1 ? 'Kaminofen' : 'Kaminöfen'}${matched.length > visible ? ` – ${visible} angezeigt` : ''}${incompleteNotice}` : 'Wählen Sie mindestens einen Filter';
    empty.hidden = !shouldShow || matched.length !== 0;
    more.hidden = !shouldShow || visible >= matched.length;
    activeFilters.replaceChildren(...activeGroups.flatMap(({ active }) => active.map((button) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.textContent = `${button.textContent.trim()} ×`;
      chip.addEventListener('click', () => { button.setAttribute('aria-pressed', 'false'); resultLimit = 12; apply(); updateUrlState(); });
      return chip;
    })));
    reset.disabled = activeGroups.length === 0 && sort.value === defaultSort && !finderOpen;
    emit('filter', { filters: activeGroups.map(({ group, active }) => ({ group: group.dataset.filterGroup, values: active.map((button) => button.dataset.filterValue) })), results: visible });
  };

  const sortCards = () => {
    const mode = sort.value;
    orderedCards = [...cards].sort((a, b) => {
      const indexOrder = Number(a.dataset.index) - Number(b.dataset.index);
      if (mode === 'price-asc' || mode === 'price-desc') {
        const priceA = Number(a.dataset.price);
        const priceB = Number(b.dataset.price);
        const safeA = Number.isFinite(priceA) ? priceA : Number.POSITIVE_INFINITY;
        const safeB = Number.isFinite(priceB) ? priceB : Number.POSITIVE_INFINITY;
        return (mode === 'price-asc' ? safeA - safeB : safeB - safeA) || indexOrder;
      }
      if (mode === 'power-asc' || mode === 'power-desc') {
        const valuesA = powerValuesOf(a);
        const valuesB = powerValuesOf(b);
        const powerA = valuesA.length ? (mode === 'power-asc' ? Math.min(...valuesA) : Math.max(...valuesA)) : null;
        const powerB = valuesB.length ? (mode === 'power-asc' ? Math.min(...valuesB) : Math.max(...valuesB)) : null;
        if (powerA === null || powerB === null) return powerA === powerB ? indexOrder : powerA === null ? 1 : -1;
        return (mode === 'power-asc' ? powerA - powerB : powerB - powerA) || indexOrder;
      }
      if (mode === 'storage') return Number(b.dataset.storage !== 'none') - Number(a.dataset.storage !== 'none') || indexOrder;
      return indexOrder;
    });
    orderedCards.forEach((card) => grid.append(card));
    emit('sort', { mode });
  };

  const updateUrlState = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('msp_filter');
    url.searchParams.delete('msp_sort');
    url.searchParams.delete('msp_open');
    if (sort.value !== defaultSort) url.searchParams.set('msp_sort', sort.value);
    if (finderOpen) url.searchParams.set('msp_open', '1');
    groups.forEach((group) => activeFor(group).forEach((button) => {
      url.searchParams.append('msp_filter', `${group.dataset.filterGroup}:${button.dataset.filterValue}`);
    }));
    window.history.pushState(window.history.state, '', url);
  };

  const restoreUrlState = () => {
    const url = new URL(window.location.href);
    root.querySelectorAll('[data-filter-value][aria-pressed="true"]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
    const requestedSort = url.searchParams.get('msp_sort');
    const allowedSorts = [...sort.options].map((option) => option.value);
    sort.value = allowedSorts.includes(requestedSort) ? requestedSort : defaultSort;
    url.searchParams.getAll('msp_filter').forEach((entry) => {
      const separator = entry.indexOf(':');
      if (separator < 1) return;
      const groupName = entry.slice(0, separator);
      const value = entry.slice(separator + 1);
      const group = groups.find((item) => item.dataset.filterGroup === groupName);
      const button = group && [...group.querySelectorAll('[data-filter-value]')].find((item) => item.dataset.filterValue === value);
      if (button) button.setAttribute('aria-pressed', 'true');
    });
    const hasFilters = groups.some((group) => activeFor(group).length > 0);
    finderOpen = url.searchParams.get('msp_open') === '1' || hasFilters || sort.value !== defaultSort;
    resultLimit = 12;
    sortCards();
    apply();
  };

  root.addEventListener('click', (event) => {
    const filter = event.target.closest('[data-filter-value]');
    if (filter) {
      const ownGroup = filter.closest('[data-filter-group]');
      if (ownGroup) {
        filter.setAttribute('aria-pressed', filter.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      } else {
        const value = filter.dataset.filterValue;
        const groupName = groupNameFor(value);
        const group = root.querySelector(`[data-filter-group="${groupName}"]`);
        if (!group) return;
        group.querySelectorAll('[data-filter-value]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.filterValue === value)));
      }
      apply();
      updateUrlState();
      if (ownGroup) closeGroup(ownGroup, true);
      if (filter.hasAttribute('data-scroll-results')) root.querySelector('#msp-stove-results').scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const groupToggle = event.target.closest('[data-group-toggle]');
    if (groupToggle) {
      const panel = groupToggle.nextElementSibling;
      const open = groupToggle.getAttribute('aria-expanded') !== 'true';
      if (open) closeGroups(groupToggle.closest('[data-filter-group]'));
      groupToggle.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      return;
    }
    const tracked = event.target.closest('[data-stove-track]');
    if (tracked) emit(tracked.dataset.stoveTrack, { href: tracked.getAttribute('href') || '' });
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-filter-group]')) closeGroups();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openGroup = groups.find((group) => group.querySelector('[data-group-toggle]')?.getAttribute('aria-expanded') === 'true');
    if (!openGroup) return;
    event.preventDefault();
    closeGroup(openGroup, true);
  });

  reset.addEventListener('click', () => {
    root.querySelectorAll('[data-filter-value][aria-pressed="true"]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
    finderOpen = false;
    resultLimit = 12;
    sort.value = defaultSort;
    sortCards();
    apply();
    updateUrlState();
  });
  sort.addEventListener('change', () => {
    finderOpen = true;
    resultLimit = 12;
    sortCards();
    apply();
    updateUrlState();
  });
  window.addEventListener('popstate', restoreUrlState);

  root.querySelectorAll('[data-filter-value]').forEach((button) => {
    if (!cards.some((card) => matches(card, button))) button.hidden = true;
  });
  groups.forEach((group) => {
    if (![...group.querySelectorAll('[data-filter-value]')].some((button) => !button.hidden)) group.hidden = true;
  });
  more.addEventListener('click', () => {
    resultLimit += 12;
    apply();
  });
  root.querySelectorAll('[data-open-finder]').forEach((button) => button.addEventListener('click', () => {
    finderOpen = true;
    resultLimit = 12;
    apply();
    updateUrlState();
    resultsHead.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }));
  root.querySelectorAll('[data-slider-next]').forEach((button) => button.addEventListener('click', () => {
    const slider = root.querySelector(`[data-slider="${button.dataset.sliderNext}"]`);
    slider?.scrollBy({ left: slider.clientWidth * .82, behavior: 'smooth' });
  }));
  const showcase = root.querySelector('[data-stove-showcase]');
  if (showcase) {
    const tabs = [...showcase.querySelectorAll('[data-stove-product-tab]')];
    const panels = [...showcase.querySelectorAll('[data-stove-product-panel]')];
    const activateTab = (name, moveFocus = false) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.stoveProductTab === name;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && moveFocus) tab.focus({ preventScroll: true });
      });
      panels.forEach((panel) => {
        const active = panel.dataset.stoveProductPanel === name;
        panel.hidden = !active;
        if (active) panel.querySelector('[data-stove-product-row]').scrollLeft = 0;
      });
      emit('product-tab', { tab: name });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(tab.dataset.stoveProductTab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        activateTab(tabs[nextIndex].dataset.stoveProductTab, true);
      });
    });
    showcase.querySelector('[data-stove-showcase-next]')?.addEventListener('click', () => {
      showcase.querySelector('[data-stove-product-panel]:not([hidden]) [data-stove-product-row]')?.scrollBy({ left: showcase.clientWidth * .82, behavior: 'smooth' });
    });
    activateTab('recommended');
  }
  restoreUrlState();
})();
